import UserDriverEditParams, {
  UserDriverEditParamsAddress,
  UserDriverEditParamsDriver,
  UserDriverEditParamsTaxiDriverAssociation,
  UserDriverEditParamsTaxiDriverAssociationForB2B,
  UserDriverEditParamsTaxiDriverAssociationForEmployment,
  UserDriverEditParamsUser,
} from "../../../../../../common/services/user/driver/edit/user-driver-edit-params";
import AddressType from "../../../../../../common/types/address-type";
import UserCitizenship from "../../../../../../common/types/user-citizenship";
import UserFormOfEmployment from "../../../../../../common/types/user-form-of-employment";
import UserLanguage from "../../../../../../common/types/user-language";
import UserVehicleOwnership from "../../../../../../common/types/user-vehicle-ownership-form";
import dateService from "../../../../../../common/utils/date/date.service";
import DriverEditAccountFormData from "../form/types/driver-edit-account-form-data";
import DriverEditCompanyFormData from "../form/types/driver-edit-company-form-data";
import DriverEditVehicleFormData from "../form/types/driver-edit-vehicle-form-data";
import TaxiDriverEditUserFormData from "../form/types/taxi-driver-edit-user-form-data";
import DriverEditAddress from "../types/driver-edit-address";
import DriverEditAddressType from "../types/driver-edit-address-type";
import DriverEditCarOwnershipType from "../types/driver-edit-car-ownership-type";
import DriverEditCitizenshipType from "../types/driver-edit-citizenship-type";
import DriverEditFormOfEmploymentType from "../types/driver-edit-form-of-employment-type";
import DriverEditLanguageType from "../types/driver-edit-language-type";

const driverEditParamsAddressType = (
  type: DriverEditAddressType,
): AddressType => {
  switch (type) {
    case DriverEditAddressType.MAILING:
      return AddressType.MAILING;
    case DriverEditAddressType.STARTING:
      return AddressType.STARTING;
  }
};

const driverEditParamsContractType = (
  type: DriverEditFormOfEmploymentType | null,
): UserFormOfEmployment | undefined => {
  switch (type) {
    case DriverEditFormOfEmploymentType.B2B:
      return UserFormOfEmployment.B2B;
    case DriverEditFormOfEmploymentType.EMPLOYMENT:
      return UserFormOfEmployment.EMPLOYMENT;
    default:
      return undefined;
  }
};

const driverEditParamsCarOwnershipType = (
  type: DriverEditCarOwnershipType,
): UserVehicleOwnership => {
  switch (type) {
    case DriverEditCarOwnershipType.BUSINESS:
      return UserVehicleOwnership.BUSINESS;
    case DriverEditCarOwnershipType.OWNER:
      return UserVehicleOwnership.OWNER;
  }
};

const driverEditParamsCitizenshipType = (
  type: DriverEditCitizenshipType,
): UserCitizenship => {
  switch (type) {
    case DriverEditCitizenshipType.BY:
      return UserCitizenship.BY;
    case DriverEditCitizenshipType.CZ:
      return UserCitizenship.CZ;
    case DriverEditCitizenshipType.DE:
      return UserCitizenship.DE;
    case DriverEditCitizenshipType.LT:
      return UserCitizenship.LT;
    case DriverEditCitizenshipType.PL:
      return UserCitizenship.PL;
    case DriverEditCitizenshipType.RU:
      return UserCitizenship.RU;
    case DriverEditCitizenshipType.SK:
      return UserCitizenship.SK;
    case DriverEditCitizenshipType.UA:
      return UserCitizenship.UA;
  }
};

const driverEditParamsLanguageType = (
  type: DriverEditLanguageType,
): UserLanguage => {
  switch (type) {
    case DriverEditLanguageType.BY:
      return UserLanguage.BY;
    case DriverEditLanguageType.CZ:
      return UserLanguage.CZ;
    case DriverEditLanguageType.DE:
      return UserLanguage.DE;
    case DriverEditLanguageType.LT:
      return UserLanguage.LT;
    case DriverEditLanguageType.PL:
      return UserLanguage.PL;
    case DriverEditLanguageType.RU:
      return UserLanguage.RU;
    case DriverEditLanguageType.SK:
      return UserLanguage.SK;
    case DriverEditLanguageType.UA:
      return UserLanguage.UA;
    case DriverEditLanguageType.EN:
      return UserLanguage.EN;
    case DriverEditLanguageType.ES:
      return UserLanguage.ES;
    case DriverEditLanguageType.FR:
      return UserLanguage.FR;
  }
};

const driverEditParamsLanguagesType = (
  types: DriverEditLanguageType[],
): UserLanguage[] => {
  return types.map(driverEditParamsLanguageType);
};

const createDriverEditParamsAddress = (
  userDataAddress: DriverEditAddress,
): UserDriverEditParamsAddress => {
  const paramsAddress: UserDriverEditParamsAddress = {
    apartment: userDataAddress.apartmentNumber,
    country: userDataAddress.country.trim(),
    description: (userDataAddress.description ?? "").trim(),
    houseNo: userDataAddress.houseNumber.trim(),
    lat: userDataAddress.latitude,
    lon: userDataAddress.longitude,
    street: userDataAddress.street.trim(),
    town: userDataAddress.town.trim(),
    type: driverEditParamsAddressType(userDataAddress.type),
    zipCode: userDataAddress.zipCode.trim(),
  };

  return paramsAddress;
};

const createDriverEditParamsAddresses = (
  userDataAddresses: DriverEditAddress[],
): UserDriverEditParamsAddress[] => {
  return userDataAddresses.map(createDriverEditParamsAddress);
};

const createDriverEditTaxiDriverAssociationForEmploymentRequest = (
  userFormData: TaxiDriverEditUserFormData,
  taxiDriverAssociationUuid: string,
): UserDriverEditParamsTaxiDriverAssociationForEmployment => {
  return {
    contractType: driverEditParamsContractType(userFormData.formOfEmployment)!,
    fleetPartnerId: userFormData.fleetPartner?.uuid!,
    id: taxiDriverAssociationUuid,
  };
};

const createDriverEditTaxiDriverAssociationForB2BRequest = (
  userFormData: TaxiDriverEditUserFormData,
  companyFormData: DriverEditCompanyFormData,
  taxiDriverAssociationUuid: string,
): UserDriverEditParamsTaxiDriverAssociationForB2B => {
  return {
    contractType: driverEditParamsContractType(userFormData.formOfEmployment)!,
    taxId: companyFormData.taxNumber,
    regon: companyFormData.companyId || null,
    krsNo: companyFormData.nationalCourtRegisterNumber || null,
    fleetPartnerId: userFormData.fleetPartner?.uuid ?? null,
    companyName: companyFormData.name,
    country: companyFormData.country,
    town: companyFormData.town,
    zipCode: companyFormData.zipCode,
    street: companyFormData.street,
    houseNo: companyFormData.houseNumber,
    apartment: companyFormData.apartmentNumber,
    description: companyFormData.description,
    id: taxiDriverAssociationUuid,
  };
};

const createDriverEditTaxiDriverAssociationRequest = (
  userFormData: TaxiDriverEditUserFormData,
  companyFormData: DriverEditCompanyFormData,
  taxiDriverAssociationUuid: string,
): UserDriverEditParamsTaxiDriverAssociation => {
  if (userFormData.formOfEmployment === DriverEditFormOfEmploymentType.B2B) {
    return createDriverEditTaxiDriverAssociationForB2BRequest(
      userFormData,
      companyFormData,
      taxiDriverAssociationUuid,
    );
  }

  return createDriverEditTaxiDriverAssociationForEmploymentRequest(
    userFormData,
    taxiDriverAssociationUuid,
  );
};

const createDriver = (
  userFormData: TaxiDriverEditUserFormData,
  vehicleFormData: DriverEditVehicleFormData,
): UserDriverEditParamsDriver => {
  return {
    birthDate: dateService.format(userFormData.birthDate!, "yyyy-mm-dd"),
    birthPlace: userFormData.birthPlace,
    idNumber: userFormData.personalIdNumber,
    citizenship: driverEditParamsCitizenshipType(userFormData.citizenship!),
    languages: driverEditParamsLanguagesType(userFormData.languages),
    experience: userFormData.yearsOfExperience!,
    carOwnership: driverEditParamsCarOwnershipType(vehicleFormData.ownership!),
    carMake: vehicleFormData.make,
    carModel: vehicleFormData.model,
    carRegNo: vehicleFormData.registrationNumber,
    carProdYear: vehicleFormData.productionYear!,
    carSeatsNo: vehicleFormData.numberOfSeats!,
  };
};

const createUser = (
  accountFormData: DriverEditAccountFormData,
  userFormData: TaxiDriverEditUserFormData,
): UserDriverEditParamsUser => {
  return {
    firstName: userFormData.firstName,
    lastName: userFormData.lastName,
    mobilePrimaryPrefix: userFormData.mobile.dialingCode ?? "",
    mobilePrimary: userFormData.mobile.number ?? "",
    mobileSecondaryPrefix: userFormData.alternativeMobile.dialingCode,
    mobileSecondary: userFormData.alternativeMobile.number,
    email: accountFormData.email,
  };
};

const createSubmitFormParams = (
  accountFormData: DriverEditAccountFormData,
  userFormData: TaxiDriverEditUserFormData,
  vehicleFormData: DriverEditVehicleFormData,
): UserDriverEditParams => {
  return {
    user: createUser(accountFormData, userFormData),
    driver: createDriver(userFormData, vehicleFormData),
    associations: [],
    addresses: createDriverEditParamsAddresses(userFormData.addresses),
  };
};

const taxiDriverEditParamsFactory = {
  createSubmitFormParams,
};

export default taxiDriverEditParamsFactory;
